import React, { useEffect, useRef, useState } from 'react';
import { gsap } from '../../../../gsap';
import './Joints.scss';

function ApolloJoints({ screenWidth, screenHeight }) {
  const apolloJointsRef = useRef();
  const apolloJointsCanvas = useRef();
  const apolloJointsUpper = useRef();
  const apolloJointsLower = useRef();
  const apolloJointsAnimation = useRef();
  const upperCopyRef = useRef();
  const shouldersRef = useRef();
  const elbowsRef = useRef();
  const wristsRef = useRef();
  const anklesRef = useRef();
  const hipsRef = useRef();
  const kneesRef = useRef();
  const images = useRef([]);
  const bar = useRef();
  const copy1 = useRef();
  const copy2 = useRef();
  const copy3 = useRef();
  const [canvasWidth, setCanvasWidth] = useState();
  const [canvasHeight, setCanvasHeight] = useState();
  const [upperCopyHeight, setUpperCopyHeight] = useState();

  //tracks upper copy height on resize
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0].contentRect.height;
      if (height !== upperCopyHeight) {
        setUpperCopyHeight(height);
      }
    });
    console.log(upperCopyHeight);
    resizeObserver.observe(upperCopyRef.current);
  }, [upperCopyHeight]);

  useEffect(() => {
    const widthD = Math.min(screenWidth * 0.3, 492);
    const innerHeight = window.innerHeight * 0.9;
    const heightM = screenWidth * 1.777;
    const heightD = Math.min(screenWidth * 0.3 * 1.777, 492 * 1.777);
    //mobile
    if (screenWidth < 481) {
      if (innerHeight < heightM) {
        setCanvasHeight(innerHeight);
        setCanvasWidth(innerHeight * 0.56);
      } else {
        setCanvasWidth(screenWidth);
        setCanvasHeight(heightM);
      }
      //desktop
    } else {
      if (upperCopyHeight > innerHeight) {
        setCanvasWidth(upperCopyHeight * 0.56);
        setCanvasHeight(upperCopyHeight);
      } else if (innerHeight < heightD) {
        setCanvasHeight(innerHeight);
        setCanvasWidth(innerHeight * 0.56);
      } else {
        setCanvasWidth(widthD);
        setCanvasHeight(heightD);
      }
    }
  }, [screenWidth, screenHeight, upperCopyHeight]);

  //bar animation for lower copy
  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: apolloJointsLower.current,
        start: 'top 60%'
      }
    });
    screenWidth > 1024
      ? tl.to(bar.current, {
          height: 'calc(100% - 10vw)',
          duration: 1.5
        })
      : tl.to(bar.current, {
          height: '100%',
          duration: 1.5
        });

    const textTl = gsap.timeline();
    textTl
      .to(copy1.current, { opacity: 1, duration: 0.5 })
      .to(copy2.current, { opacity: 1, duration: 0.5 })
      .to(copy3.current, { opacity: 1, duration: 0.5 });

    tl.add(textTl, '<');
  }, [screenWidth]);

  useEffect(() => {
    const playOnScroll = () => {
      images.current = [];
      // set canvas properties
      const canvas = apolloJointsCanvas.current.getContext('2d');
      // Set the size of the canvas

      apolloJointsCanvas.current.width = canvasWidth;
      apolloJointsCanvas.current.height = canvasHeight;

      // Count the frames and set the path to the images
      const frameCount = 20;
      const currentFrame = (index, screenSize) =>
        `https://morgan-stanley-creating-space-cdn.s3.amazonaws.com/apollo/joints/${screenSize}/A7L_JOINT_${(
          index + 1
        )
          .toString()
          .padStart(2, '0')}.png`;
      // Set start frame

      const joints = {
        frame: 0
      };
      const tl = gsap.timeline();
      tl.to(shouldersRef.current, {
        color: '#FFFFFF',
        opacity: 1,
        duration: 0.7
      })
        .to(shouldersRef.current, {
          color: '#afafaf',
          duration: 0.1
        })
        .to(
          elbowsRef.current,
          {
            color: '#FFFFFF',
            opacity: 1,
            duration: 0.7
          },
          '<'
        )
        .to(elbowsRef.current, {
          color: '#afafaf',
          duration: 0.1
        })
        .to(
          wristsRef.current,
          {
            color: '#FFFFFF',
            opacity: 1,
            duration: 0.7
          },
          '<'
        )
        .to(wristsRef.current, {
          color: '#afafaf',
          duration: 0.1
        })
        .to(
          anklesRef.current,
          {
            color: '#FFFFFF',
            opacity: 1,
            duration: 0.7
          },
          '<'
        )
        .to(anklesRef.current, {
          color: '#afafaf',
          duration: 0.1
        })
        .to(
          hipsRef.current,
          {
            color: '#FFFFFF',
            opacity: 1,
            duration: 0.7
          },
          '<'
        )
        .to(hipsRef.current, {
          color: '#afafaf',
          duration: 0.1
        })
        .to(
          kneesRef.current,
          {
            color: '#FFFFFF',
            opacity: 1,
            duration: 0.7
          },
          '<'
        );

      // Go through frames from start to finish

      const jointAnim = gsap.timeline({
        scrollTrigger: {
          trigger: apolloJointsUpper.current,
          start: 'top top',
          endTrigger: apolloJointsUpper.current,
          end: 'bottom bottom',
          pin: true,
          scrub: 1
        }
      });

      if (screenWidth < 481) {
        for (let i = 0; i < frameCount; i++) {
          const img = new Image();
          img.src = currentFrame(i, 'mobile');
          images.current.push(img);
        }
      } else {
        for (let i = 0; i < frameCount; i++) {
          const img = new Image();
          img.src = currentFrame(i, 'desktop');
          images.current.push(img);
        }
      }

      jointAnim.add('start').to(
        joints,
        {
          frame: frameCount - 1,
          snap: 'frame',
          duration: 5,
          ease: 'linear',
          onUpdate: render
        },
        'start'
      );
      jointAnim.add(tl, '<');

      images.current[0].onload = render;

      function render() {
        canvas.clearRect(0, 0, apolloJointsCanvas.current.width, apolloJointsCanvas.current.height);
        canvas.drawImage(
          images.current[joints.frame],
          0,
          0,
          apolloJointsCanvas.current.width,
          apolloJointsCanvas.current.height
        );
      }
    };

    playOnScroll();
  }, [screenHeight, screenWidth, canvasWidth, canvasHeight, upperCopyHeight]);

  return (
    <section className="joints joints-apollo" ref={apolloJointsRef}>
      <div className="joints__upper joints__upper-apollo" ref={apolloJointsUpper}>
        <div className="joints__upper__contents">
          <div className="joint__animation joint__animation-apollo" ref={apolloJointsAnimation}>
            <canvas className="joints__animation__canvas" ref={apolloJointsCanvas} />
          </div>
          <div className="joints__upper__copy" ref={upperCopyRef}>
            <p className="joints__upper__copy--title">
              A7L suits had accordion-shaped joints in the
            </p>
            <div className="joints__upper__copy--items">
              <p className="joints__upper__copy--item" ref={shouldersRef}>
                shoulders
              </p>
              <p className="joints__upper__copy--item" ref={elbowsRef}>
                elbows
              </p>
              <p className="joints__upper__copy--item" ref={wristsRef}>
                wrists
              </p>
              <p className="joints__upper__copy--item" ref={anklesRef}>
                ankles
              </p>
              <p className="joints__upper__copy--item" ref={hipsRef}>
                hips
              </p>
              <p className="joints__upper__copy--item" ref={kneesRef}>
                and knees
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="joints__lower joints__lower-apollo" ref={apolloJointsLower}>
        <div className="copyWithBar__bar copyWithBar__bar-apollo" ref={bar} />
        <div className="joints__lower__text">
          <p className="joints__lower__text--small" ref={copy1}>
            These synthetic rubber joints were hand-dipped and designed by a latex manufacturer
            better known for making bras.
          </p>
          <p className="joints__lower__text--big" ref={copy2}>
            Joint stiffness made it difficult for astronauts, especially those with shorter arms, to
            reach their own suit components.
          </p>
          <p className="joints__lower__text--small" ref={copy3}>
            Astronauts sometimes had to either push one another's switches and buttons.
          </p>
          <div />
        </div>
      </div>
    </section>
  );
}

export default ApolloJoints;
